import get from "lodash/fp/get";
import { createSelector } from "reselect";
import { TemplateId, TemplateType } from "../../globalTypes";
import { getFunctionDict } from "../functions/selectors";
import { RootState } from "../types";
import { getIsTopLevel } from "./thunks/utils";
import { TemplateMetadataSTORE, TemplateMetadataStoreSlice } from "./types";

const passModuleId = (state: RootState, moduleId: TemplateId): TemplateId =>
  moduleId;

const passTemplateIds = (
  state: RootState,
  templateIds: TemplateId[]
): TemplateId[] => templateIds;

// Slice
export const getTemplateMetadataSlice = (
  state: RootState
): TemplateMetadataStoreSlice => state.templateMetadata;

export const getAllTemplateMetadataById = (state: RootState) =>
  getTemplateMetadataSlice(state).byId;

export const getAllVisibleOverviewIds = (state: RootState) =>
  getTemplateMetadataSlice(state).visibleOverviewIds;

// isFetching
export const getIsFetchingAll = (state: RootState) =>
  getTemplateMetadataSlice(state).isFetchingAll;

export const getTemplateMetadataFromId = (
  state: RootState,
  id?: TemplateId | null
) => {
  if (!id) return undefined;
  return getAllTemplateMetadataById(state)[id];
};

export const getTemplateMetadataFromIds = (
  state: RootState,
  ids: number[]
): (TemplateMetadataSTORE | undefined)[] =>
  ids.map((id) => getTemplateMetadataFromId(state, id));

export const getIsMetadataFetchingForTemplateId = (
  state: RootState,
  id: TemplateId
) => get("isFetching", getTemplateMetadataFromId(state, id)) || false;

export const getIsMetadataFetchingForTemplateIds = (
  state: RootState,
  ids: TemplateId[]
) => ids.some((id) => getIsMetadataFetchingForTemplateId(state, id));

// Template

export const getDetailTemplateIdById = (state: RootState, id: TemplateId) =>
  getTemplateMetadataFromId(state, id)?.detailId || undefined;

export const getDefaultSearchQueryForTemplateId = (
  state: RootState,
  id: TemplateId
) => get("defaultSearchQuery", getTemplateMetadataFromId(state, id));

export const getTemplateNameForTemplateId = (
  state: RootState,
  id?: TemplateId | null
) => {
  const templateName = get("name", getTemplateMetadataFromId(state, id));
  const displayAsName = get("displayAs", getTemplateMetadataFromId(state, id));

  return displayAsName || templateName;
};

export const getDatasourceIdForTemplateId = (
  s: RootState,
  id: TemplateId | null
) => {
  if (id === null) return undefined;
  return getTemplateMetadataFromId(s, id)?.dataSourceId;
};

export const getHasItemsForTemplateId = (
  state: RootState,
  id?: TemplateId | null
) => getTemplateMetadataFromId(state, id)?.hasItems;

export const getTypeForTemplateId = (
  state: RootState,
  id?: TemplateId | null
): TemplateType | undefined => getTemplateMetadataFromId(state, id)?.type;

export const getFunctionIdsForTemplateId = (
  state: RootState,
  id: TemplateId | null
) => getTemplateMetadataFromId(state, id)?.functionIds;

export const getDoesTemplateHaveNoCommonFunctions = createSelector(
  getFunctionIdsForTemplateId,
  getFunctionDict,
  (functionIds, functionsByFunctionId) => {
    if (!functionIds) return false;

    const someFunctionsDefined = functionIds.some(
      (functionId) => !!functionsByFunctionId[functionId]
    );

    const nonCommonFunctions = functionIds.filter(
      (functionId) => functionsByFunctionId[functionId]?.isCommon === false
    );

    return someFunctionsDefined && !!nonCommonFunctions.length;
  }
);

export const getTemplateDetailIdsForTemplateId = (
  state: RootState,
  id?: TemplateId | null
) => getTemplateMetadataFromId(state, id)?.detailIds;

// Templates
export const getIdsOfAllTemplates = createSelector(
  getAllTemplateMetadataById,
  (templateIds) => Object.keys(templateIds).map(Number)
);

export const getIdsOfVisibleTemplates = createSelector(
  getIdsOfAllTemplates,
  getAllTemplateMetadataById,
  (templateIds, templateMetadataById) =>
    templateIds.filter((id) => templateMetadataById[id]?.visible)
);

export const getIdsOfAllTopLevelTemplates = createSelector(
  getIdsOfAllTemplates,
  getAllTemplateMetadataById,
  (templateIds, templateMetadataById) => {
    const isOverviewTemplate = (id: TemplateId): boolean => {
      const { type } = templateMetadataById[id];
      return getIsTopLevel(type);
    };

    return templateIds.filter(isOverviewTemplate);
  }
);

export const getTemplateNamesForGivenIds = createSelector(
  passTemplateIds,
  getAllTemplateMetadataById,
  (templateIds, templatesMetadataById) => {
    type ForTemplateIDType = { templateId: TemplateId; templateName: string };
    const forTemplateID = (templateId: TemplateId): ForTemplateIDType => ({
      templateId,
      templateName: templatesMetadataById[templateId]?.name || "",
    });

    return templateIds
      .filter((templateId) => templatesMetadataById[templateId]?.hasItems)
      .map(forTemplateID);
  }
);

export const getTemplatesNameAndOrderForModuleId = createSelector(
  passModuleId,
  getAllTemplateMetadataById,
  (moduleId, templatesMetada) => {
    const templatesMetadataForModule = Object.values(templatesMetada).filter(
      (template) =>
        template.moduleId === moduleId && getIsTopLevel(template.type)
    );

    return templatesMetadataForModule.map((metadata) => ({
      id: metadata?.id ?? 0,
      order: metadata?.order ?? 0,
      displayName: metadata?.name,
    }));
  }
);

export const getHasFunctionsForTemplate = createSelector(
  getFunctionIdsForTemplateId,
  getFunctionDict,
  (functionIds, functionsData) => {
    // template metadata was not yet fetched or template dont have any fncs
    if (!functionIds) return true;

    return functionIds.every((functionID) => !!functionsData[functionID]);
  }
);
